import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Activity, Cpu, Wifi, WifiOff, Zap, Thermometer, Droplets, Container, GitBranchPlus, ArrowDownToLine, Wind, Bell, Tag, Radio } from "lucide-react";
import { StatusCircle, PressureGauge, TankGraphic } from "../components/acms/Indicators";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// Generic 4–20 mA loop convention, used as a best-effort fallback for analog sensors that
// don't have per-device engineering-unit scaling wired into ingestion yet.
const SENSOR_MIN = 4;
const SENSOR_MAX = 20;

function sensorPct(value) {
  if (value == null || isNaN(value)) return null;
  if (value >= 0 && value <= 100 && (value < SENSOR_MIN || value > SENSOR_MAX)) return value;
  return Math.min(100, Math.max(0, ((value - SENSOR_MIN) / (SENSOR_MAX - SENSOR_MIN)) * 100));
}

const TREND_ENABLED_TYPES = new Set(["pressure_sensor", "tank"]);

const TYPE_META = {
  pump:             { label: "Pump Status",         icon: Activity,        iconColor: "text-orange-400" },
  pressure_sensor:  { label: "Pressure Sensors",    icon: Activity,        iconColor: "text-sky-400" },
  tank:             { label: "Tanks",                icon: Container,       iconColor: "text-sky-400" },
  valve:            { label: "Valves",               icon: GitBranchPlus,   iconColor: "text-emerald-400" },
  lift:             { label: "Lifts",                icon: ArrowDownToLine, iconColor: "text-pink-400" },
  fan:              { label: "Fans & AHU",           icon: Wind,            iconColor: "text-cyan-400" },
  fire_alarm_device:{ label: "Fire Alarm Devices",   icon: Bell,            iconColor: "text-red-400" },
};
const TYPE_ORDER = ["pump", "pressure_sensor", "tank", "valve", "lift", "fan", "fire_alarm_device"];

const SIGNAL_LABEL = {
  DI: "Digital In",
  AI: "Analog In",
  DO: "Digital Out",
  digital_output: "Digital Out",
  digital_input: "Digital In",
  analog_input: "Analog In",
};

// ─── IO mapping footer strip — shown at the bottom of every wired asset card ─

function IOBadge({ mapping, asset }) {
  if (!mapping && !asset.io_point_name) return null;
  const sigLabel = mapping ? (SIGNAL_LABEL[mapping.signal_type] || mapping.signal_type) : null;
  const unit = mapping?.engineering_unit;
  const addr = mapping?.io_point_address;
  const desc = mapping?.description;
  const range = mapping && (mapping.engineering_min != null && mapping.engineering_max != null)
    ? `${mapping.engineering_min}–${mapping.engineering_max}${unit ? ` ${unit}` : ""}`
    : null;

  return (
    <div className="mt-3 pt-2.5 border-t border-gray-100 space-y-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* Register name */}
        <span className="flex items-center gap-1 font-mono text-[11px] text-orange-500 font-semibold">
          <Tag className="w-3 h-3" strokeWidth={2.5} />
          {asset.io_point_name}
        </span>
        {/* Signal type chip */}
        {sigLabel && (
          <span className="px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-600 text-[10px] font-semibold uppercase tracking-wide">
            {sigLabel}
          </span>
        )}
        {/* Modbus address */}
        {addr && (
          <span className="flex items-center gap-1 text-[10px] text-gray-500">
            <Radio className="w-3 h-3" strokeWidth={2} />
            Addr&nbsp;{addr}
          </span>
        )}
        {/* Engineering range */}
        {range && (
          <span className="text-[10px] text-gray-400">Range: {range}</span>
        )}
        {/* Gateway IMEI */}
        {asset.gateway_id && (
          <span className="text-[10px] text-gray-400 font-mono truncate">GW: {asset.gateway_id}</span>
        )}
      </div>
      {/* Human-readable description from the IO mapping */}
      {desc && (
        <p className="text-[10px] text-gray-500 leading-snug">{desc}</p>
      )}
    </div>
  );
}

// ─── Card wrapper ────────────────────────────────────────────────────────────

function Card({ title, subtitle, ioMapping, asset, children, testid }) {
  return (
    <div className="glass p-5 rounded-lg" data-testid={testid}>
      <div className="mb-4">
        <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-600">{title}</h3>
        {subtitle && <p className="text-[11px] text-gray-500 mt-0.5 truncate">{subtitle}</p>}
      </div>
      {children}
      {asset && <IOBadge mapping={ioMapping} asset={asset} />}
    </div>
  );
}

// ─── Unlinked badge ───────────────────────────────────────────────────────────

function UnlinkedBadge({ asset, reason }) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-200 text-slate-600">
        {reason === "no_data" ? "Awaiting data" : "Not wired to live data"}
      </span>
      <span className="text-xs text-gray-500">Static status: <b className="text-gray-700">{asset.status || "active"}</b></span>
    </div>
  );
}

// ─── Pump card ────────────────────────────────────────────────────────────────

function PumpCard({ asset, live, ioMapping }) {
  if (!live) return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  const running = Number(live.value) === 1;
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <div className="flex justify-center py-1">
        <StatusCircle status={running ? "running" : "stopped"} subtitle={running ? "Running" : "Stopped"} />
      </div>
    </Card>
  );
}

// ─── ch123/ch124 — legacy 4–20 mA gauges ─────────────────────────────────────

const CH124_CABLE_LENGTH_M = 10;
const CH124_TANK_HEIGHT_M = 0.4;
const CH124_TANK_MAX_LITRES = 20;

function LegacyArcGauge({ value, unit, pct, arcColor, rangeLabel }) {
  const circumference = Math.PI * 72;
  const arcStroke = pct !== null ? (pct / 100) * circumference : 0;
  return (
    <div className="relative max-w-[240px] mx-auto">
      <svg viewBox="0 0 200 120" className="w-full">
        <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="14" strokeLinecap="round" />
        <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke={arcColor} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={`${arcStroke} ${circumference}`} style={{ transition: "stroke-dasharray 0.6s, stroke 0.4s" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
        <div className="text-3xl font-extrabold text-gray-900 leading-none">{value.toFixed(2)}</div>
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{unit}</div>
        <div className="text-sm font-bold mt-0.5" style={{ color: arcColor }}>{pct.toFixed(1)}%</div>
        <div className="text-[9px] text-gray-500">{rangeLabel}</div>
      </div>
    </div>
  );
}

function LegacyPressureCard({ asset, live, ioMapping }) {
  if (!live) return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  const value = Number(live.value);
  const pct = sensorPct(value);
  const arcColor = pct < 10 ? "#ef4444" : pct < 30 ? "#f59e0b" : "#22c55e";
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <LegacyArcGauge value={value} unit={live.unit || "bar"} pct={pct} arcColor={arcColor} rangeLabel="of 4–20 range" />
    </Card>
  );
}

function LegacyWaterTankCard({ asset, live, ioMapping }) {
  if (!live) return (
    <Card title="Water Tank" subtitle={`ch124 · ${CH124_TANK_MAX_LITRES} L capacity`} asset={asset} ioMapping={ioMapping}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  const raw = Number(live.value);
  const litres = Math.min(CH124_TANK_MAX_LITRES, Math.max(0,
    ((raw - SENSOR_MIN) / (SENSOR_MAX - SENSOR_MIN)) * CH124_CABLE_LENGTH_M / CH124_TANK_HEIGHT_M * CH124_TANK_MAX_LITRES
  ));
  const pct = (litres / CH124_TANK_MAX_LITRES) * 100;
  return (
    <Card title="Water Tank" subtitle={`ch124 · ${CH124_TANK_MAX_LITRES} L capacity`} asset={asset} ioMapping={ioMapping}>
      <div className="flex flex-col items-center gap-2 py-1">
        <TankGraphic value={pct} label="Water Tank" liters={Math.round(litres)} />
      </div>
    </Card>
  );
}

// ─── Pressure sensor card ─────────────────────────────────────────────────────

function PressureCard({ asset, live, ioMapping }) {
  if (asset.io_point_name === "ch124") return <LegacyWaterTankCard asset={asset} live={live} ioMapping={ioMapping} />;
  if (asset.io_point_name === "ch123") return <LegacyPressureCard asset={asset} live={live} ioMapping={ioMapping} />;
  if (!live) return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  const value = Number(live.value);
  const unit = live.unit || "bar";
  const max = asset.meta?.range_bar || 16;
  const threshold = asset.meta?.alarm_low_bar || 0;
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <div className="max-w-[240px] mx-auto">
        <PressureGauge value={value} max={max} threshold={threshold} label={unit} critical={threshold > 0} />
      </div>
    </Card>
  );
}

// ─── Tank card ────────────────────────────────────────────────────────────────

function TankCard({ asset, live, ioMapping }) {
  if (!live) return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <UnlinkedBadge asset={asset} reason={asset.gateway_id ? "no_data" : "unlinked"} />
    </Card>
  );
  const value = Number(live.value);
  const pct = sensorPct(value);
  const capacityKl = asset.meta?.capacity_kl;
  const capacityLitres = capacityKl ? capacityKl * 1000 : null;
  if (pct === null) {
    return (
      <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
        <div className="text-center py-6">
          <div className="text-2xl font-extrabold text-gray-900">{value}</div>
          <div className="text-xs text-gray-500">{live.unit || ""}</div>
        </div>
      </Card>
    );
  }
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <div className="flex justify-center py-1">
        <TankGraphic value={pct} label={asset.name} liters={capacityLitres ? Math.round(capacityLitres * pct / 100) : null} />
      </div>
    </Card>
  );
}

// ─── Trend card ───────────────────────────────────────────────────────────────

function TrendCard({ selectedSite, asset, unit }) {
  const [hist, setHist] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!selectedSite || !asset.io_point_name) return;
    let cancelled = false;
    const load = () => {
      api.get(`/v1/sites/${selectedSite}/telemetry/parameter-history`, {
        params: { parameter: asset.io_point_name, hours: 24 },
      }).then((r) => {
        if (cancelled) return;
        setHist((r.data || []).map((d) => ({ ...d, t: new Date(d.ingested_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })));
        setLoaded(true);
      }).catch(() => { if (!cancelled) setLoaded(true); });
    };
    load();
    const i = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [selectedSite, asset.io_point_name]);

  return (
    <div className="w-full">
      <div className="mb-2">
        <h3 className="font-semibold text-sm uppercase tracking-wider text-gray-600">{asset.name}</h3>
        <p className="text-[11px] text-gray-500 mt-0.5">24h trend</p>
      </div>
      {!loaded ? (
        <div className="h-[160px] flex items-center justify-center text-xs text-gray-400">Loading…</div>
      ) : hist.length === 0 ? (
        <div className="h-[160px] flex items-center justify-center text-xs text-gray-400">Accumulating history…</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={hist}>
            <CartesianGrid stroke="rgba(0,0,0,0.06)" strokeDasharray="3 3" />
            <XAxis dataKey="t" hide />
            <YAxis stroke="#94a3b8" fontSize={9} width={28} />
            <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}${unit ? ` ${unit}` : ""}`, ""]} />
            <Line type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={1.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Generic status card for valve / lift / fan / fire_alarm_device ───────────

function StatusCard({ asset, live, ioMapping }) {
  if (!live) {
    return (
      <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
        <div className="flex justify-center py-1">
          <StatusCircle status={asset.status} subtitle={asset.status || "active"} />
        </div>
      </Card>
    );
  }
  const on = Number(live.value) === 1;
  return (
    <Card title={asset.name} subtitle={asset.location_label} asset={asset} ioMapping={ioMapping}>
      <div className="flex justify-center py-1">
        <StatusCircle status={on ? "active" : "fault"} subtitle={on ? "ON" : "OFF"} />
      </div>
    </Card>
  );
}

const CARD_BY_TYPE = {
  pump: PumpCard,
  pressure_sensor: PressureCard,
  tank: TankCard,
  valve: StatusCard,
  lift: StatusCard,
  fan: StatusCard,
  fire_alarm_device: StatusCard,
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function LiveMonitor() {
  const { selectedSite, sites } = useSiteContext();
  const [assets, setAssets] = useState([]);
  const [liveValues, setLiveValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [gatewayId, setGatewayId] = useState(null);
  const [allGateways, setAllGateways] = useState([]);
  const [ioMap, setIoMap] = useState({});
  const [gwStatus, setGwStatus] = useState(null);
  const [sysvolt, setSysvolt] = useState(null);
  const [systemp, setSystemp] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchAssets = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    try {
      const r = await api.get("/assets", { params: { site_id: selectedSite } });
      setAssets(r.data || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [selectedSite]);

  const fetchLiveValues = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    try {
      const r = await api.get("/assets/live-values", { params: { site_id: selectedSite } });
      setLiveValues(r.data || {});
      setLastUpdate(new Date());
    } catch { /* ignore */ }
  }, [selectedSite]);

  const fetchGatewayHealth = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    try {
      const r = await api.get(`/v1/sites/${selectedSite}/telemetry/snapshot`);
      const sv = r.data.find((d) => d.parameter === "sysvolt");
      const st = r.data.find((d) => d.parameter === "systemp");
      setSysvolt(sv ? sv.value : null);
      setSystemp(st ? st.value : null);
      const first = r.data.find((d) => d.gateway_id);
      if (first?.gateway_id) setGatewayId(first.gateway_id);
    } catch { /* ignore */ }
  }, [selectedSite]);

  const fetchAllGateways = useCallback(async () => {
    if (!selectedSite || selectedSite === "all") return;
    try {
      const r = await api.get("/hardware/gateways");
      const gws = (r.data || []).filter((g) => g.site_id === selectedSite);
      setAllGateways(gws);

      // Fetch IO mappings for every gateway and build a flat lookup
      const mappingResults = await Promise.all(
        gws.map((gw) =>
          api.get(`/hardware/gateways/${gw.gateway_id}/io-mappings`)
            .then((res) => ({ gatewayId: gw.gateway_id, rows: res.data || [] }))
            .catch(() => ({ gatewayId: gw.gateway_id, rows: [] }))
        )
      );
      const map = {};
      for (const { gatewayId: gwId, rows } of mappingResults) {
        for (const m of rows) {
          if (m.io_point_name) map[`${gwId}:${m.io_point_name}`] = m;
        }
      }
      setIoMap(map);
    } catch { /* ignore */ }
  }, [selectedSite]);

  useEffect(() => {
    if (!selectedSite || selectedSite === "all") return;
    setLoading(true);
    setAssets([]);
    setLiveValues({});
    setAllGateways([]);
    setIoMap({});
    fetchAssets();
    fetchLiveValues();
    fetchGatewayHealth();
    fetchAllGateways();
    const i = setInterval(() => { fetchLiveValues(); fetchGatewayHealth(); }, 5000);
    const j = setInterval(fetchAssets, 30000);
    const k = setInterval(fetchAllGateways, 30000);
    return () => { clearInterval(i); clearInterval(j); clearInterval(k); };
  }, [selectedSite, fetchAssets, fetchLiveValues, fetchGatewayHealth, fetchAllGateways]);

  useEffect(() => {
    if (!gatewayId) return;
    const load = () => api.get(`/v1/gateways/${gatewayId}/status`).then((r) => setGwStatus(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [gatewayId]);

  const currentSite = sites.find((s) => s.site_id === selectedSite);

  if (!selectedSite || selectedSite === "all") {
    return <div className="text-gray-500 p-8">Select a site to view live telemetry.</div>;
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="h-3 bg-gray-100 rounded w-32 mb-2" />
          <div className="h-9 bg-gray-100 rounded w-72 mb-2" />
          <div className="h-2.5 bg-gray-50 rounded w-56" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="glass rounded-lg p-5 h-40 bg-gray-50" />)}
        </div>
      </div>
    );
  }

  const byType = {};
  for (const a of assets) (byType[a.asset_type] ||= []).push(a);
  const hasAnyAssets = assets.length > 0;

  return (
    <div className="space-y-6" data-testid="acms-live-monitor">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-orange-400">ACMS Live Monitor</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{currentSite?.name || selectedSite}</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time telemetry for registered assets · auto-refresh 5 s</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {gwStatus && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold ${gwStatus.connected ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
              {gwStatus.connected ? <Wifi className="w-3.5 h-3.5" strokeWidth={2.5} /> : <WifiOff className="w-3.5 h-3.5" strokeWidth={2.5} />}
              {gwStatus.connected ? "Device Connected" : "Device Offline"}
            </div>
          )}
          {lastUpdate && (
            <div className="glass px-4 py-2 rounded-lg text-xs text-gray-500">
              Last data: <span className="text-emerald-400 font-semibold">{lastUpdate.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </div>

      {!hasAnyAssets && (
        <div className="glass-strong rounded-lg px-5 py-3 text-sm text-amber-400 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse inline-block" />
          No assets registered for this site yet. Add pumps, pressure sensors, tanks etc. from the Asset Registries
          in the sidebar to see them here.
        </div>
      )}

      {/* Gateway Health */}
      {(sysvolt !== null || systemp !== null) && (
        <section>
          <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-violet-400" strokeWidth={2.5} /> Gateway Health
          </h2>
          <div className="grid grid-cols-2 gap-4 max-w-xs">
            {sysvolt !== null && (
              <div className="glass rounded-lg p-5 flex flex-col items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" strokeWidth={2.5} />
                <div className="text-2xl font-extrabold text-gray-900">{sysvolt.toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Volts (sysvolt)</div>
              </div>
            )}
            {systemp !== null && (
              <div className="glass rounded-lg p-5 flex flex-col items-center gap-2">
                <Thermometer className="w-5 h-5 text-orange-400" strokeWidth={2.5} />
                <div className="text-2xl font-extrabold text-gray-900">{systemp.toFixed(1)}</div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500">°C (systemp)</div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* One section per asset type */}
      {TYPE_ORDER.map((type) => {
        const list = byType[type];
        if (!list || !list.length) return null;
        const meta = TYPE_META[type];
        const Icon = meta.icon;
        const CardComp = CARD_BY_TYPE[type];
        const trendEnabled = TREND_ENABLED_TYPES.has(type);
        return (
          <section key={type}>
            <h2 className="text-sm uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
              <Icon className={`w-4 h-4 ${meta.iconColor}`} strokeWidth={2.5} /> {meta.label}
            </h2>
            {trendEnabled ? (
              <div className="space-y-6">
                {list.map((asset) => {
                  const live = liveValues[asset.asset_id];
                  const hasTrend = asset.gateway_id && asset.io_point_name;
                  const ioMapping = asset.gateway_id && asset.io_point_name
                    ? ioMap[`${asset.gateway_id}:${asset.io_point_name}`]
                    : null;
                  return (
                    <div key={asset.asset_id} className="w-full">
                      <CardComp asset={asset} live={live} ioMapping={ioMapping} />
                      {hasTrend && (
                        <div className="mt-3">
                          <TrendCard selectedSite={selectedSite} asset={asset} unit={live?.unit} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
                {list.map((asset) => {
                  const ioMapping = asset.gateway_id && asset.io_point_name
                    ? ioMap[`${asset.gateway_id}:${asset.io_point_name}`]
                    : null;
                  return (
                    <CardComp key={asset.asset_id} asset={asset} live={liveValues[asset.asset_id]} ioMapping={ioMapping} />
                  );
                })}
              </div>
            )}
          </section>
        );
      })}

      {/* Gateway Devices */}
      <section className="glass-strong rounded-lg p-4 border border-orange-500/10" data-testid="gateway-status-bar">
        <h2 className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-2">
          <Cpu className="w-4 h-4 text-orange-400" strokeWidth={2.5} /> Gateway Devices
        </h2>
        {allGateways.length === 0 && !gatewayId ? (
          <div className="text-xs text-gray-400">No gateways registered for this site.</div>
        ) : (
          <div className="space-y-2">
            {(allGateways.length > 0 ? allGateways : [{ gateway_id: gatewayId, gateway_name: null, hardware_model: null }]).map((gw) => (
              <div key={gw.gateway_id} className="flex flex-wrap items-center gap-4 text-xs py-2 px-3 rounded-lg bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-gray-500">IMEI:</span>
                  <span className="font-mono text-orange-400 font-semibold">{gw.gateway_id}</span>
                  {gw.gateway_name && <span className="text-gray-500 truncate">· {gw.gateway_name}</span>}
                  {gw.hardware_model && <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] font-medium">{gw.hardware_model}</span>}
                </div>
                {gw.gateway_id === gatewayId && gwStatus && (
                  <>
                    <div>
                      <span className={gwStatus.connected ? "text-emerald-500 font-bold" : "text-red-400 font-bold"}>
                        {gwStatus.connected ? "● Connected" : "● Offline"}
                      </span>
                    </div>
                    {gwStatus.peer_ip && (
                      <div><span className="text-gray-500">IP:</span> <span className="text-gray-700 font-mono">{gwStatus.peer_ip}</span></div>
                    )}
                    {gwStatus.last_seen_at && (
                      <div><span className="text-gray-500">Last seen:</span> <span className="text-gray-700">{new Date(gwStatus.last_seen_at).toLocaleTimeString()}</span></div>
                    )}
                  </>
                )}
                {sysvolt !== null && gw.gateway_id === gatewayId && (
                  <div><span className="text-gray-500">Voltage:</span> <span className="text-gray-700 font-bold">{sysvolt.toFixed(2)} V</span></div>
                )}
                {systemp !== null && gw.gateway_id === gatewayId && (
                  <div><span className="text-gray-500">Temp:</span> <span className="text-gray-700 font-bold">{systemp.toFixed(1)} °C</span></div>
                )}
                {/* IO mapping count for this gateway */}
                {Object.values(ioMap).filter((m) => m.gateway_id === gw.gateway_id).length > 0 && (
                  <div className="ml-auto text-gray-400">
                    {Object.values(ioMap).filter((m) => m.gateway_id === gw.gateway_id).length} IO points mapped
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {lastUpdate && (
          <div className="mt-2 text-[10px] text-gray-400 text-right">Last data: {lastUpdate.toLocaleTimeString()}</div>
        )}
      </section>
    </div>
  );
}
